import { type IntlShape } from 'react-intl'

import { type PimProductWithVariantsV1 } from '@redteclab/api/clients/bully'

import {
  priceGetFormattedPercentageDiscount,
  priceGetFormattedPrice,
} from '../../../../price'
import { type SeoGlobalConfigContextValue } from '../../../../seo'
import { type ProductPrices } from '../../../components/ProductBuyBox/hooks/useProductPrices'
import { PRODUCT_TEMPLATES } from '../../../model/productTemplates'

/**
 * for details:
 * https://jira.shop-apotheke.com/browse/WSLAM-459
 *
 * if product contains meta_title, or meta_description, it is used instead of default ones.
 * it as well may contain placeholders, which are replaced with values from product.
 */
export const getOverriddenMetaTags = ({
  content,
  intl,
  prices,
  product,
  productTemplate,
  seoConfig,
}: {
  content: string
  intl: IntlShape
  prices: ProductPrices
  product: PimProductWithVariantsV1
  productTemplate: PRODUCT_TEMPLATES
  seoConfig: SeoGlobalConfigContextValue
}): string => {
  const formattedPrice = priceGetFormattedPrice(
    prices.displayPrice?.price ?? 0,
    intl,
  )

  const prescription =
    productTemplate === PRODUCT_TEMPLATES.RX
      ? intl.formatMessage({ id: 'prescription.labels.prescriptionRequired' })
      : intl.formatMessage({ id: 'presciprion.label.prescriptionFree' })

  const mapping: Record<string, string | null | undefined> = {
    // we don't have a date object in the product
    Date: '',
    Discount: prices.discount
      ? priceGetFormattedPercentageDiscount(prices.discount)
      : '',
    Disposability: product.stock_status,
    OurBrand: seoConfig.seoShopName,
    // we don't have a page number
    PageNumber: '',
    Prescription: prescription,
    Price: formattedPrice,
    ProductName: product.name,
    Quantity: `${product.package_size?.quantity ?? ''} ${
      product.package_size?.unit ?? ''
    }`,
    // we don't have shipping costs in the product
    ShippingCost: '',
    Unity: product.pharma_form?.label,
  }

  /**
   * the passed content (e.g. product.meta_title) may contain placeholders (e.g. {ProductName})
   * this regex gets such placeholders ({word}) and replaces them with the values from the mapping
   */
  return content.replaceAll(
    /\{(?<name>\w+)\}/gu,
    (match: string, name: string): string => mapping[name] ?? '',
  )
}
